"use client";

import { useQuery } from '@tanstack/react-query';
import { FooterData } from '@repo/ts';

interface UseFooterLinksOptions {
  lang?: string;
}

interface UseFooterLinksReturn {
  footerData: FooterData | null;
  loading: boolean;
  error: string | null;
}

// Query keys for footer data
const footerKeys = {
  all: ['footer'] as const,
  links: (lang: string) => [...footerKeys.all, 'links', lang] as const,
};

async function fetchFooterLinks(lang: string = 'en'): Promise<FooterData> {
  const response = await fetch(`/api/footer?lang=${lang}`, {
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch footer links: ${response.status}`);
  }

  return response.json();
}

export function useFooterLinks({ lang = 'en' }: UseFooterLinksOptions = {}): UseFooterLinksReturn {
  const {
    data: footerData,
    isLoading,
    error,
  } = useQuery<FooterData, Error>({
    queryKey: footerKeys.links(lang),
    queryFn: () => fetchFooterLinks(lang),
    staleTime: 60 * 60 * 1000, // 1 hour - footer links don't change often
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
  });

  return {
    footerData: footerData || null,
    loading: isLoading,
    error: error?.message || null,
  };
}